import { useState, useEffect, useCallback } from 'react';
import {
  VoiceOption,
  KnowledgeBaseOption,
  DocumentOption,
  CallRouteOption,
  PhoneNumberOption,
} from '../types';
import { ApiClient } from '@utils/apiClient';

export const useAgentOptions = (currentPhoneNumberId?: string | null) => {
  const [voices, setVoices] = useState<VoiceOption[]>([]);
  const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBaseOption[]>([]);
  const [documents, setDocuments] = useState<DocumentOption[]>([]);
  const [callRoutes, setCallRoutes] = useState<CallRouteOption[]>([]);
  const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumberOption[]>([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchVoices = useCallback(async () => {
    try {
      const response = await ApiClient.get('/agents/agent-voices/');
      if (response.ok) {
        const data: VoiceOption[] = await response.json();
        setVoices(data);
      }
    } catch (err) {
      // Error handled silently
    }
  }, []);

  const fetchKnowledgeBases = useCallback(async () => {
    try {
      const response = await ApiClient.get('/knowledge-base/');
      if (response.ok) {
        const data: KnowledgeBaseOption[] = await response.json();
        setKnowledgeBases(data);
      }
    } catch (err) {
      // Error handled silently
    }
  }, []);

  const fetchDocuments = useCallback(async (knowledgeBaseId?: string) => {
    try {
      const url = knowledgeBaseId
        ? `/knowledge-base/documents/?knowledge_base=${knowledgeBaseId}`
        : '/knowledge-base/documents/';
      const response = await ApiClient.get(url);
      if (response.ok) {
        const data: DocumentOption[] = await response.json();
        setDocuments(data);
      }
    } catch (err) {
      // Error handled silently
    }
  }, []);

  const fetchCallRoutes = useCallback(async () => {
    try {
      const response = await ApiClient.get('/telephony/call-routes/');
      if (response.ok) {
        const data: CallRouteOption[] = await response.json();
        setCallRoutes(data);
      }
    } catch (err) {
      // Error handled silently
    }
  }, []);

  const fetchPhoneNumbers = useCallback(async () => {
    try {
      const response = await ApiClient.get('/telephony/phone-numbers/?status=not_in_use');
      if (response.ok) {
        const data: PhoneNumberOption[] = await response.json();

        // If editing and agent has a phone number, fetch it and add to list if not already present
        if (currentPhoneNumberId) {
          const hasCurrentNumber = data.some(phone => phone.id === currentPhoneNumberId);
          if (!hasCurrentNumber) {
            try {
              const currentResponse = await ApiClient.get(
                `/telephony/phone-numbers/${currentPhoneNumberId}/`
              );
              if (currentResponse.ok) {
                const currentPhone: PhoneNumberOption = await currentResponse.json();
                setPhoneNumbers([...data, currentPhone]);
                return;
              }
            } catch (err) {
              // If fetching current phone number fails, just use the unused numbers
            }
          }
        }

        setPhoneNumbers(data);
      }
    } catch (err) {
      // Error handled silently
    }
  }, [currentPhoneNumberId]);

  const fetchAllOptions = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      await Promise.all([
        fetchVoices(),
        fetchKnowledgeBases(),
        fetchCallRoutes(),
        fetchPhoneNumbers(),
      ]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch options');
    } finally {
      setLoading(false);
    }
  }, [fetchVoices, fetchKnowledgeBases, fetchCallRoutes, fetchPhoneNumbers]);

  useEffect(() => {
    fetchAllOptions();
  }, [fetchAllOptions]);

  return {
    voices,
    knowledgeBases,
    documents,
    callRoutes,
    phoneNumbers,
    loading,
    error,
    fetchDocuments,
    refetch: fetchAllOptions,
  };
};
